'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { usePointerDrag } from '@/hooks/usePointerDrag';
import type { Drone } from '@/types/fleet';
import type { MissionType } from '@/lib/mission-type';
import { CoverageAttachWizard } from '../missions/_components/CoverageAttachWizard';
import type { MissionListStatus } from '../missions/_components/missionDetailShared';
import { clampDockSheetWidth } from './CopDockSheet';

/**
 * ATTACHING COVERAGE TO A MISSION THE OPERATOR JUST DREW, without leaving the COP and without
 * covering it.
 *
 * Creating a mission on the COP has worked since the convergence plan's phases 3 and 4
 * (`CopMissionCompose`), and attaching coverage to it has not - the wizard was mounted in exactly
 * one place, `MissionCoveragePanel`, so setting up a held station meant drawing the route here and
 * then navigating to `/missions/[id]` to say it should be held. That is the same context switch
 * this whole feature exists to remove, at the one moment the operator is configuring the station.
 *
 * IT IS A SHEET AND NOT A MODAL, and that is the whole of this file's reason to exist. The first
 * cut of this surface was a 720 px `Dialog` over the map, which contradicted compose's own premise
 * one press after compose had honoured it: "the route is drawn on the live COP map ... An operator
 * planning a sortie could not see the air they were planning into." The station being configured
 * IS the last waypoint of the route just drawn, and the dialog put it behind a scrim while its
 * radius was typed as a bare number. Worse, the contradiction was load-bearing: because a modal
 * would have hidden pre_takeoff and the bay opening, compose REFUSED to hand off at all on the NOW
 * lane and toasted the operator to the mission page instead. One checkbox, three outcomes.
 *
 * In the column none of that holds. The map stays whole, so the hand-off happens on both lanes and
 * the checkbox means one thing.
 *
 * THE WIZARD IS OPENED WHOLE, NOT FLATTENED INTO COMPOSE. Its own contract is "three tabs over ONE
 * form, all three reachable at any time, every field pre-filled", with each step header carrying
 * the count of refusals belonging to it and a live projection beside it. A linear fourth step in
 * compose would lose all of that, and its header says why that matters: "A linear wizard would
 * make the common case slower than the form it replaces, which is how a redesign loses to the
 * thing it fixed." So compose carries an INTENT - one checkbox - and the wizard arrives here.
 *
 * THE TWO CALLS STAY SEPARATELY RECOVERABLE. `POST /api/coverage` takes a `mission_id`, so
 * "create with coverage" is two requests in order and never one. If the attach fails or the
 * operator changes their mind, they are left over a mission that EXISTS and is visible on the
 * board behind them - a state the product already understands - rather than in a create that
 * rolled back because a second form was cancelled.
 */

/**
 * What did not happen, said out loud, wherever the operator left from - and how to finish it.
 *
 * THE ACTION REOPENS THIS SHEET; it does not navigate. The first cut sent the operator to
 * `/missions/[id]`, which is a page load away from the live air picture at the one moment they are
 * most likely to have left: mid-launch, to reach the dock phase readout or the aircraft's telemetry
 * in the other two sheets, or the queue's own STOP. Leaving the COP is the context switch this
 * whole surface exists to remove, so the recovery stays on the COP.
 *
 * Deliberately not phrased as a failure. Standing the panel down to watch your own takeoff is a
 * correct thing to do, and a toast that reads like a reprimand for it teaches operators to dismiss
 * toasts.
 */
function warnCoverageNotAttached(onReopen: () => void) {
  toast.warning(
    'Coverage is not attached yet. The mission stands; the station is not being held.',
    { action: { label: 'Finish', onClick: onReopen } },
  );
}

interface CopCoverageAttachSheetProps {
  /** The mission that was just created. Null closes the sheet. */
  target: {
    missionId: string;
    missionType: MissionType;
    hold: { latitude: number; longitude: number; altitude: number } | null;
    /** What compose assigned it, seeded as the first rotation member. */
    assignedDroneId: string | null;
    /** The commit was a LAUNCH. Changes what this sheet says happened, nothing else. */
    launched: boolean;
  } | null;
  drones: Drone[];
  dronesStatus: MissionListStatus;
  onRetryDrones: () => void;
  capableTypes: MissionType[] | null;
  onClose: () => void;
  /** Coverage attached - refresh the board so the new chip and block appear. */
  onAttached: () => void;
  /**
   * Put this sheet back, from the toast that fires when it went away unattached.
   *
   * The page owns the target, so only it can restore one. Takes the target back rather than
   * reading page state, so a reopen cannot resurrect a DIFFERENT mission's attach.
   */
  onReopen: (target: NonNullable<CopCoverageAttachSheetProps['target']>) => void;
  /** Shared with every other right-column sheet - one column, one width. */
  width: number;
  onWidthChange: (width: number) => void;
}

export function CopCoverageAttachSheet({
  target, drones, dronesStatus, onRetryDrones, capableTypes, onClose, onAttached, onReopen,
  width, onWidthChange,
}: CopCoverageAttachSheetProps) {
  const [submitting, setSubmitting] = useState(false);
  const headingId = useId();
  /**
   * FOCUS HAS TO BE PUT SOMEWHERE, because nothing the operator pressed still exists.
   *
   * This panel is not opened by a press on itself - compose commits, compose unmounts taking the
   * focused button with it, and `document.activeElement` falls back to `<body>`. A keyboard
   * operator's next Tab would then start at the top of the COP and traverse the whole map chrome
   * to reach a form that appeared by itself. The `Dialog` this replaces did this for free; a plain
   * div does not, so it is done explicitly.
   */
  const panelRef = useRef<HTMLDivElement>(null);
  /** Set by the wizard's own success path, so a dismiss can tell the two cases apart. */
  const attachedRef = useRef(false);
  const missionId = target?.missionId ?? null;

  /**
   * THE ROSTER IS RE-READ WHEN THIS OPENS.
   *
   * `canvasRefData` is loaded once, when compose opens, and the operator may have been drawing for
   * several minutes since. Step 2 of the wizard shows per-aircraft readiness computed from that
   * list, so a stale one can present an aircraft that has since gone airborne or offline as a
   * valid rotation member - and the rotation would then be configured around it.
   */
  useEffect(() => {
    if (missionId !== null) {
      attachedRef.current = false;
      onRetryDrones();
    }
  }, [missionId, onRetryDrones]);

  // On the panel itself rather than its first tab: the heading names it, so a screen reader
  // announces WHAT appeared before the operator is dropped inside a three-tab form.
  useEffect(() => {
    if (missionId !== null) panelRef.current?.focus();
  }, [missionId]);

  /**
   * ESCAPE STILL CLOSES IT, because the `Dialog` this replaces gave that for free.
   *
   * Scoped to this panel rather than the window: the page's own capture-phase Escape disarms the
   * MAP mode, and a window listener here would race it - an operator pressing Escape to stop
   * placing points would also lose their coverage form. Bound on the panel, so it only answers
   * while focus is inside, and it does not stop propagation for anything else.
   */
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || submitting) return;
    e.stopPropagation();
    dismiss();
  };

  const startWidth = useRef(width);
  const { onPointerDown: onResizePointerDown } = usePointerDrag({
    cursor: 'ew-resize',
    onDragStart: () => {
      startWidth.current = width;
    },
    onDrag: ({ dx }) => onWidthChange(clampDockSheetWidth(startWidth.current - dx, window.innerWidth)),
  });

  /**
   * THE WARNING IS OWED ON EVERY EXIT, not only on the X.
   *
   * `dismiss` fires from one button. This sheet lives in the missions slot, and the selection
   * reducer clears `missionsPanelOpen` on SELECT_DOCK, OPEN_DETAILS and SET_DETAILS_SEGMENT - so
   * clicking a dock or an aircraft unmounts it. On the NOW lane that is not an idle risk, it is the
   * likely path: the operator has just commanded a takeoff, and the dock phase readout and the
   * aircraft's telemetry are in the OTHER two sheets. Watching their own launch used to take the
   * coverage sheet away with no toast, no marker and no trace, leaving a launched held station with
   * no rotation on it and nothing on screen that said so.
   *
   * A ref, not `target`/`submitting`: this must run on the real unmount with the values as they
   * were, and an effect that depended on either would fire the warning on every close instead.
   */
  const warnRef = useRef<{ reopen: () => void } | null>(null);
  // Kept current by an effect rather than written during render: a ref assignment in the render
  // body is a React lint error, and this one has to reflect the LAST committed render anyway.
  useEffect(() => {
    // The thunk ONLY. It used to carry an `attached` snapshot too, and that snapshot was the
    // HIGH: taken on the last committed render, it was still `false` when a successful attach
    // unmounted the sheet in the same commit. The cleanup reads `attachedRef.current` live now,
    // so keeping the field would be dead code that looks like the authoritative flag.
    warnRef.current = target === null ? null : { reopen: () => onReopen(target) };
  });
  useEffect(() => () => {
    const pending = warnRef.current;
    // THE FLAG IS READ AT CLEANUP, NOT SNAPSHOTTED (#685 review HIGH).
    //
    // `warnRef` used to carry an `attached` copy taken by the effect above, on the LAST
    // COMMITTED render. A
    // successful attach never gets another one: `onAttached` does `attachedRef.current = true`
    // (a ref write, so no render) and then `onClose()`, which sets `coverageAttachTarget` to
    // null - and page.tsx renders this sheet CONDITIONALLY, so the component unmounts in that
    // same commit. Nothing runs between the two, `pending.attached` is still the `false` from
    // the commit where `submitting` flipped, and the cleanup fired the warning.
    //
    // The operator therefore got "Continuous coverage attached" and "Coverage is not attached
    // yet. The mission stands; the station is not being held." stacked on top of each other,
    // with a Finish button that reopens a wizard over a mission that already has coverage - a
    // second ATTACH then 409s. A C2 surface contradicting itself about whether a station is
    // held is worse than the silence this warning was written to replace, and it teaches
    // operators to ignore the one toast that catches the real miss.
    //
    // `attachedRef.current` is the live value at unmount and cannot go stale this way.
    if (pending && !attachedRef.current) warnCoverageNotAttached(pending.reopen);
  }, []);

  const dismiss = () => {
    if (target === null) return;
    // NOT WHILE AN ATTACH IS IN FLIGHT, on the paths this panel owns - the X and Escape. A dismiss
    // mid-request would take away the surface reporting whether the rotation was created. The
    // request is bounded (`ATTACH_TIMEOUT_MS` in the wizard), so this can never hold the column
    // hostage.
    //
    // IT IS NOT A GUARANTEE ABOUT THE COLUMN. The tab strip can still unmount this panel mid-POST,
    // which the `Dialog` this replaces made impossible by rendering the page inert behind a scrim.
    // The outcome is not lost when that happens - the wizard's success and failure toasts are
    // global - but the panel that would have shown it is gone.
    if (submitting) return;
    // WHAT DID NOT HAPPEN, SAID OUT LOUD. The operator ticked a box asking for this station to be
    // held; closing by reflex would otherwise leave a mission with no coverage, nothing on screen
    // saying so, and no route back except knowing to go to the mission page. The mission page is
    // still the only other place coverage can be attached - the COP's own coverage block reads,
    // it does not command - so the action stays.
    if (!attachedRef.current) warnCoverageNotAttached(() => onReopen(target));
    // The unmount warning must not fire a second time for the same exit.
    warnRef.current = null;
    onClose();
  };

  if (target === null) return null;

  return (
    <div
      ref={panelRef}
      // Named and reachable. The enclosing landmark is still called "Missions", which is the wrong
      // name for what is on screen, so this region carries its own.
      role="region"
      aria-labelledby={headingId}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      /* Compose's shell, verbatim, because this is compose's slot. `overflow-clip` and never
         `overflow-hidden`: `hidden` makes this a SCROLL CONTAINER that renders no scrollbar and
         ignores the wheel while still being scrollable programmatically, which is how a panel ends
         up off screen with no way for the operator to bring it back. */
      className={`
        flex flex-col min-h-0 overflow-clip
        lg:w-[var(--dock-sheet-width)] lg:shrink-0 lg:relative lg:z-auto lg:h-auto
        fixed bottom-0 left-0 right-0 z-40 max-w-[100vw]
        rounded-t-xl border-t border-subtle bg-popover/90 backdrop-blur-sm
        shadow-sheet
        lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none lg:backdrop-blur-none
      `}
    >
      <button
        type="button"
        aria-label="Resize coverage panel"
        onPointerDown={onResizePointerDown}
        className="hidden lg:block absolute inset-y-0 left-0 z-10 w-2 cursor-ew-resize border-0 bg-transparent p-0 hover:bg-status-blue/30 focus:outline-none focus-visible:bg-primary"
      />

      <div className="flex-1 min-h-0 flex flex-col max-h-[min(70vh,44rem)] lg:max-h-full bg-card/60">
        <div className="flex items-center justify-between px-3 py-1.5 border-b divider shrink-0">
          <h2 id={headingId} className="text-[11px] uppercase tracking-wider text-foreground font-normal m-0">
            Hold this station
          </h2>
          <button
            type="button"
            onClick={dismiss}
            disabled={submitting}
            aria-label="Close coverage panel"
            className="p-1.5 -my-0.5 -mx-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <X className="size-4" strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>

        {/* `relative` because this scrolls, and a scroll container that is not a containing block
            does not contain the absolutely positioned boxes inside it - they escape its clip and
            keep their un-scrolled static position. Anything abspos in the wizard (live regions,
            its inputs) belongs to this scroller. */}
        <div className="relative flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
          {/* WHAT ACTUALLY HAPPENED, per lane. "The mission is created" was written when only the
              plan lane could reach this surface; on the NOW lane the launch POST has been accepted
              and an aircraft is being commanded off the pad, which is not a moment to understate. */}
          <p className="text-[11px] leading-snug text-muted-foreground">
            {target.launched
              ? 'Mission LAUNCHED. Configure the rotation that will relieve it on station - or close this and attach coverage later from the mission page.'
              : 'The mission is created. Configure the rotation that will keep the station manned - or close this and attach coverage later from the mission page.'}
          </p>
          <CoverageAttachWizard
            missionId={target.missionId}
            missionType={target.missionType}
            hold={target.hold}
            assignedDroneId={target.assignedDroneId}
            drones={drones}
            dronesStatus={dronesStatus}
            onRetryDrones={onRetryDrones}
            capableTypes={capableTypes}
            busy={false}
            submitting={submitting}
            onSubmittingChange={setSubmitting}
            onAttached={() => {
              attachedRef.current = true;
              // Belt and braces with the cleanup's own live read above: clearing the pending
              // warning here is what `dismiss()` already does for its exit, and it means the
              // success path cannot fire the "not attached" toast even if this component is
              // ever made to survive the close.
              warnRef.current = null;
              onAttached();
              onClose();
            }}
          />
        </div>
      </div>
    </div>
  );
}
